/**
 * Single source of truth for the Settings IA — consumed by BOTH the sidebar
 * (components/app-sidebar.tsx) and the settings hub page (app/settings/page.tsx)
 * so the two can never drift (Settings IA redesign spec §3; blast-radius
 * audit sin #6 — the hub used to duplicate the sidebar's list).
 *
 * `title` and `description` are the English text; `i18nKey` and
 * `descriptionKey` name the same words in the catalogue (messages/shell for
 * the shell's own, messages/<locale>.json for the older nav.* keys). Render
 * through `navText`, which answers in the interface language and falls back to
 * the English written here. Every group and leaf has a key, apart from product
 * names, which read the same in every language; each key's English is these
 * exact words (lib/__tests__/settings-nav-i18n.spec.ts), so English screens
 * are unchanged. `hubOnly` keeps a card on /settings without a sidebar entry.
 */
import type { LucideIcon } from 'lucide-react'
import {
  Bell,
  Building2,
  CircleUser,
  CreditCard,
  Fish,
  Gauge,
  Globe,
  ImageIcon,
  LayoutGrid,
  Mail,
  Paintbrush,
  Palette,
  RotateCw,
  ScrollText,
  Send,
  Server,
  Settings2,
  Shield,
  ShieldBan,
  ShieldCheck,
  Tag,
  UserRound,
  Users,
  UserX,
} from 'lucide-react'
import { ROUTES } from '@/lib/routes'
import { navEntryAllowed } from '@/lib/auth/nav-permissions'

export type SettingsNavLeaf = {
  title: string
  url: string
  description: string
  /** Catalogue key of `description`. */
  descriptionKey: string
  icon: LucideIcon
  /** Catalogue key of `title`; absent only for a product name. */
  i18nKey?: string
  hubOnly?: boolean
}

export type SettingsNavGroup = {
  title: string
  /** Catalogue key of `title`. */
  i18nKey: string
  items: SettingsNavLeaf[]
}

/**
 * `key` in the interface language, or `english` when there is no key or `t`
 * has no answer for it (outside the I18nProvider, or a `t` that echoes the
 * key). A raw key path never reaches the screen.
 */
export function navText(t: (key: string) => string, english: string, key?: string): string {
  if (!key) return english
  const text = t(key)
  return text && text !== key ? text : english
}

/**
 * The Settings IA with every leaf the role cannot open removed, and every
 * group left empty by that removed too (owner, 2026-10-05: menus hide what the
 * role cannot open). The permission each leaf needs lives in ONE place,
 * lib/auth/nav-permissions.ts. `permissions` is the KNOWN grant list; pass
 * `null` while it loads or after a failed fetch and the IA comes back whole
 * (fail open — never flash-hide).
 */
export function filterSettingsNav(
  groups: readonly SettingsNavGroup[],
  permissions: readonly string[] | null,
): SettingsNavGroup[] {
  if (permissions === null) return [...groups]
  return groups
    .map((group) => ({ ...group, items: group.items.filter((leaf) => navEntryAllowed(leaf.url, permissions)) }))
    .filter((group) => group.items.length > 0)
}

/** The pages of the menu behind a person's name, in its order (owner, 2 Nov 2026). */
const QUICK_SETTINGS: readonly string[] = [
  ROUTES.settings.profile,
  ROUTES.settings.team,
  ROUTES.settings.brandKit,
  ROUTES.settings.mailDeliveryHub,
  ROUTES.settings.security,
  ROUTES.settings.billing,
]

/**
 * The settings reached most often, as leaves of this tree, so their words, icons and permissions are the
 * tree's. `permissions` as in filterSettingsNav: null while it loads, and the list comes back whole. A
 * group's first page is its "Overview", which says nothing out of the tree, so it goes by the group's name.
 */
export function quickSettings(permissions: readonly string[] | null): SettingsNavLeaf[] {
  const groups = filterSettingsNav(SETTINGS_NAV, permissions)
  return QUICK_SETTINGS.flatMap((url) => {
    for (const group of groups) {
      const at = group.items.findIndex((leaf) => leaf.url === url)
      if (at === -1) continue
      const leaf = group.items[at]
      return url === ROUTES.settings.mailDeliveryHub ? [{ ...leaf, title: group.title, i18nKey: group.i18nKey }] : [leaf]
    }
    return []
  })
}

// The order the sidebar and the hub show (owner, 30 Oct 2026): Company,
// Workspace and Account first, then the delivery groups and Branding.
export const SETTINGS_NAV: SettingsNavGroup[] = [
  {
    title: 'Company',
    i18nKey: 'shell.settingsNav.groups.company',
    items: [
      {
        title: 'Company Profile',
        i18nKey: 'nav.companyProfile',
        url: ROUTES.settings.organization,
        description: 'Company name, logo, industry, and contact information',
        descriptionKey: 'shell.settingsNav.descriptions.companyProfile',
        icon: Building2,
      },
      {
        title: 'Billing & Usage',
        i18nKey: 'nav.billingUsage',
        url: ROUTES.settings.billing,
        // The AI usage lives here since 30 Oct 2026 (it left AI Settings).
        description: 'Subscription plan, usage limits, AI usage, and payment history',
        descriptionKey: 'shell.settingsNav.descriptions.billingUsage',
        icon: CreditCard,
      },
      {
        // "AI Studio" until 30 Oct 2026; that name is the generators'. The URL
        // stays, so the AI errors' "replace your key" links keep working.
        title: 'AI Settings',
        i18nKey: 'shell.settingsNav.aiSettings',
        url: ROUTES.settings.aiStudio,
        description: 'AI provider keys and the AI credits left this month',
        descriptionKey: 'shell.settingsNav.descriptions.aiSettings',
        icon: Settings2,
      },
    ],
  },
  {
    title: 'Workspace',
    i18nKey: 'nav.workspace',
    items: [
      {
        title: 'General',
        i18nKey: 'nav.general',
        url: ROUTES.settings.workspace,
        description: 'Workspace name, logo, timezone, language, and identity domains',
        descriptionKey: 'shell.settingsNav.descriptions.general',
        icon: Globe,
      },
      {
        title: 'Team Members',
        i18nKey: 'nav.teamMembers',
        url: ROUTES.settings.team,
        description: 'Invite, manage roles, and deactivate team members',
        descriptionKey: 'shell.settingsNav.descriptions.teamMembers',
        icon: Users,
      },
      {
        // Custom roles (2026-10-05): needs read:Role, like every entry here it
        // hides without its permission (lib/auth/nav-permissions.ts).
        title: 'Roles and Permissions',
        i18nKey: 'shell.settingsNav.rolesPermissions',
        url: ROUTES.settings.roles,
        description: 'See what each role can do, and create custom roles for your team',
        descriptionKey: 'shell.settingsNav.descriptions.rolesPermissions',
        icon: ShieldCheck,
      },
      {
        title: 'Security',
        i18nKey: 'nav.security',
        url: ROUTES.settings.security,
        // Branded sign-in (3 Nov 2026): the Sign-in tab holds the company's sign-in methods (SSO among them).
        description: 'Two-factor authentication, sign-in methods, sessions and passwords',
        descriptionKey: 'signInSettings.nav.securityDescription',
        icon: Shield,
      },
      {
        title: 'Audit Logs',
        i18nKey: 'shell.nav.auditLogs',
        url: ROUTES.settings.auditLogs,
        description: 'Workspace activity log — who changed what, when, across every module',
        descriptionKey: 'shell.settingsNav.descriptions.auditLogs',
        icon: ScrollText,
      },
      {
        title: 'Email Logs',
        i18nKey: 'shell.nav.emailLogs',
        url: ROUTES.settings.emailLogs,
        description: 'Every email the product sent — subject, recipient, transport, and full body',
        descriptionKey: 'shell.settingsNav.descriptions.emailLogs',
        icon: Mail,
      },
      {
        title: 'Notifications',
        i18nKey: 'shell.settingsNav.notifications',
        url: ROUTES.settings.notifications,
        description: 'Every email the platform sends: whether it is sent, its words and its look',
        descriptionKey: 'shell.settingsNav.descriptions.notifications',
        icon: Bell,
      },
      {
        title: 'Recipient Settings',
        i18nKey: 'shell.settingsNav.recipientSettings',
        url: '/recipients/settings/general',
        // #785: the page holds one thing, the profile-completeness rule; it has
        // no default fields and no uniqueness rules.
        description: "When a recipient's profile counts as complete",
        descriptionKey: 'shell.settingsNav.descriptions.recipientSettings',
        icon: Settings2,
        hubOnly: true,
      },
      {
        title: 'Suppression List',
        i18nKey: 'nav.suppressionList',
        url: '/settings/employees/suppression-list',
        description: 'Add or remove people who get no platform email; phishing simulations still reach them',
        descriptionKey: 'shell.settingsNav.descriptions.suppressionListRecipients',
        icon: UserX,
        hubOnly: true,
      },
    ],
  },
  {
    title: 'Account',
    i18nKey: 'nav.account',
    items: [
      {
        title: 'My Account',
        i18nKey: 'nav.myAccount',
        url: ROUTES.settings.profile,
        description: 'Profile, password, passkeys, 2FA, and active sessions',
        descriptionKey: 'shell.settingsNav.descriptions.myAccount',
        icon: CircleUser,
      },
    ],
  },
  {
    title: 'Delivery & Domains',
    i18nKey: 'shell.settingsNav.groups.deliveryDomains',
    items: [
      {
        title: 'Overview',
        i18nKey: 'shell.settingsNav.overview',
        url: ROUTES.settings.mailDeliveryHub,
        description: 'Overview of all mail delivery channels, domains, and policies',
        descriptionKey: 'shell.settingsNav.descriptions.overview',
        icon: LayoutGrid,
      },
      {
        title: 'SMTP Relay',
        i18nKey: 'shell.settingsNav.smtpRelay',
        url: ROUTES.settings.mailDeliverySmtp,
        description: 'Add your own SMTP relay to send from your infrastructure',
        descriptionKey: 'shell.settingsNav.descriptions.smtpRelay',
        icon: Server,
      },
      // The five connection types sit in one order everywhere — this menu, the
      // Mail Delivery hub and the transport dropdown (owner, 2026-10-29): SMTP
      // Relay, Microsoft Accounts, Google Accounts, Microsoft Admin, Google
      // Admin. An Account sends as one address its owner signed in with; an
      // Admin connection is the organisation-wide one (today's Microsoft 365
      // and Google Workspace pages, retitled; their URLs are unchanged).
      {
        title: 'Microsoft Accounts',
        i18nKey: 'shell.settingsNav.microsoftAccounts',
        url: ROUTES.settings.mailDeliveryMicrosoftAccounts,
        description: 'Send notifications as one Microsoft 365 address. No admin needed.',
        descriptionKey: 'shell.settingsNav.descriptions.microsoftAccounts',
        icon: UserRound,
      },
      {
        title: 'Google Accounts',
        i18nKey: 'shell.settingsNav.googleAccounts',
        url: ROUTES.settings.mailDeliveryGoogleAccounts,
        description: 'Send notifications as one Google Workspace address. No admin needed.',
        descriptionKey: 'shell.settingsNav.descriptions.googleAccounts',
        icon: UserRound,
      },
      {
        title: 'Microsoft Admin',
        i18nKey: 'shell.settingsNav.microsoftAdmin',
        url: ROUTES.settings.mailDeliveryMicrosoft,
        description: 'Connect your Entra app to deliver mail via Microsoft Graph',
        descriptionKey: 'shell.settingsNav.descriptions.microsoft365',
        icon: Building2,
      },
      {
        title: 'Google Admin',
        i18nKey: 'shell.settingsNav.googleAdmin',
        url: ROUTES.settings.mailDeliveryGoogle,
        description: "Use HumanFirewall's service account, or upload your own, to deliver mail via Gmail API",
        descriptionKey: 'shell.settingsNav.descriptions.googleWorkspace',
        icon: Mail,
      },
      {
        title: 'Direct Delivery',
        i18nKey: 'shell.settingsNav.directDelivery',
        url: ROUTES.settings.mailDeliveryDirectDelivery,
        description: 'Drop simulation mail straight into employee inboxes via Graph or Gmail inject',
        descriptionKey: 'shell.settingsNav.descriptions.directDelivery',
        icon: Send,
      },
      {
        title: 'Notification Domains',
        i18nKey: 'shell.nav.notificationDomains',
        url: ROUTES.settings.mailDeliveryNotificationDomains,
        description: 'Operational sending domains for transactional and notification email',
        descriptionKey: 'shell.settingsNav.descriptions.notificationDomains',
        icon: Fish,
      },
      {
        title: 'Simulation Domains',
        i18nKey: 'shell.nav.simulationDomains',
        url: ROUTES.phishing.simulationDomains,
        description: 'Phishing simulation sending domains, landing decoys, and reply capture',
        descriptionKey: 'shell.settingsNav.descriptions.simulationDomains',
        icon: Fish,
      },
      {
        title: 'Branded Domains',
        i18nKey: 'shell.settingsNav.brandedDomains',
        url: ROUTES.settings.brandedDomains,
        description: 'Custom tracking and training hostnames via CNAME',
        descriptionKey: 'shell.settingsNav.descriptions.brandedDomains',
        icon: Tag,
      },
      // NOTE (2026-07-28): the 'Delivery Defaults' leaf (/settings/delivery-defaults)
      //  was REMOVED — the module audit confirmed no code path ever read the
      //  delivery_settings it wrote, and production showed 8 company_settings rows
      //  with 0 having delivery_settings set. Leaving the nav entry behind a deleted
      //  route is worse than leaving the page. The column remains (migrations are
      //  additive).
      {
        title: 'Email Delivery',
        i18nKey: 'shell.nav.emailDelivery',
        url: ROUTES.settings.emailDelivery,
        description: 'Default sender + transport for transactional emails (PhishTrainer, Workflows), inherited Company → Workspace → module.',
        descriptionKey: 'shell.settingsNav.descriptions.emailDelivery',
        icon: Mail,
      },
    ],
  },
  {
    title: 'Delivery Policies',
    i18nKey: 'shell.settingsNav.groups.deliveryPolicies',
    items: [
      {
        title: 'Email Gate',
        i18nKey: 'shell.settingsNav.emailGate',
        url: ROUTES.settings.emailGate,
        description: 'Hold outgoing email for approval before it is sent, module by module',
        descriptionKey: 'shell.settingsNav.descriptions.emailGate',
        icon: ShieldBan,
      },
      {
        title: 'When to Send',
        i18nKey: 'shell.settingsNav.whenToSend',
        url: ROUTES.settings.mailDeliveryWhenToSend,
        description: 'Send-rate caps, business hours, timezone handling, and excluded dates',
        descriptionKey: 'shell.settingsNav.descriptions.whenToSend',
        icon: Gauge,
      },
      {
        title: 'Suppression List (View Only)',
        i18nKey: 'shell.settingsNav.suppressionListView',
        url: ROUTES.settings.mailDeliverySuppressionList,
        description: 'Who gets no platform email, read-only here; managed in Recipients',
        descriptionKey: 'shell.settingsNav.descriptions.suppressionListDelivery',
        icon: ShieldBan,
      },
      {
        title: 'Failure Handling',
        i18nKey: 'shell.settingsNav.failureHandling',
        url: ROUTES.settings.mailDeliveryFailureHandling,
        description: 'Bounce handling, retry strategy, and delivery fallback chain',
        descriptionKey: 'shell.settingsNav.descriptions.failureHandling',
        icon: RotateCw,
      },
      {
        title: 'Compliance',
        i18nKey: 'shell.settingsNav.compliance',
        url: ROUTES.settings.mailDeliveryCompliance,
        description: 'CAN-SPAM / GDPR footer, List-Unsubscribe header, and unsubscribe URL',
        descriptionKey: 'shell.settingsNav.descriptions.compliance',
        icon: ScrollText,
      },
    ],
  },
  {
    title: 'Branding',
    i18nKey: 'shell.settingsNav.groups.branding',
    items: [
      {
        title: 'Brand Kit',
        i18nKey: 'shell.settingsNav.brandKit',
        url: ROUTES.settings.brandKit,
        description: "Your company's logos, colours, fonts and voice in one place",
        descriptionKey: 'shell.settingsNav.descriptions.brandKit',
        icon: Palette,
      },
      {
        title: 'Brand Theme',
        i18nKey: 'shell.settingsNav.brandTheme',
        url: ROUTES.settings.brandTheme,
        description: 'See your brand on the admin app and the portal, and apply it for everyone',
        descriptionKey: 'shell.settingsNav.descriptions.brandTheme',
        icon: Paintbrush,
      },
      {
        title: 'Brand Logos',
        i18nKey: 'shell.settingsNav.brandLogos',
        url: ROUTES.settings.brandLogos,
        description: 'Your own logos for campaigns and announcements',
        descriptionKey: 'shell.settingsNav.descriptions.brandLogos',
        icon: ImageIcon,
      },
    ],
  },
]
